import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Package, Truck, CheckCircle, MapPin, Loader2, Clock, AlertCircle, ShoppingBag, Zap, Home } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const STEP_ICONS = [ShoppingBag, Package, Truck, Zap, MapPin, Home];

export default function TrackOrderPage() {
    const location = useLocation();
    const [orderId, setOrderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [orderStatus, setOrderStatus] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const formatTime = (timeStr) => {
        if (!timeStr) return null;
        try {
            return new Date(timeStr).toLocaleString('en-IN', {
                month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
            });
        } catch { return timeStr; }
    };

    const formatUpdated = (timeStr) => {
        if (!timeStr) return 'N/A';
        try {
            const diff = Date.now() - new Date(timeStr).getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return 'Just now';
            if (mins < 60) return `${mins} min ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            return `${Math.floor(hrs / 24)}d ago`;
        } catch { return timeStr; }
    };

    const getCompletedCount = (steps = []) => steps.filter(s => s.completed).length;
    const getTotalSteps = (steps = []) => steps.length;
    const getProgress = (steps = []) => {
        const total = getTotalSteps(steps);
        if (total === 0) return 0;
        const active = steps.findIndex(s => s.active);
        const completed = getCompletedCount(steps);
        return Math.round(((active >= 0 ? active : completed) / (total - 1)) * 100);
    };

    const handleTrack = useCallback(async (id) => {
        const trackId = id || orderId;
        if (!trackId?.trim()) return;
        setLoading(true);
        setNotFound(false);
        setOrderStatus(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${BACKEND_URL}/api/orders/${trackId.trim()}/track`, {
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                setNotFound(true);
                toast.error(data?.message || 'Order not found. Please check your Order ID.');
                return;
            }
            setOrderStatus(data.data);
        } catch {
            toast.error('Something went wrong. Please try again.');
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    // Auto-fill + auto-track when navigated from My Orders via ?orderId=...
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const qOrderId = params.get('orderId');
        if (qOrderId) {
            setOrderId(qOrderId);
            handleTrack(qOrderId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const statusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'delivered') return 'text-green-600 bg-green-50 border-green-200';
        if (s === 'shipped' || s === 'in transit') return 'text-blue-600 bg-blue-50 border-blue-200';
        if (s === 'out for delivery') return 'text-orange-600 bg-orange-50 border-orange-200';
        if (s === 'cancelled') return 'text-red-600 bg-red-50 border-red-200';
        return 'text-purple-600 bg-purple-50 border-purple-200';
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#f5f7fa] to-white">
            <SEO title="Track Order" description="Track your NSO India order status in real-time." noIndex={true} />
            {/* Hero / Search */}
            <section className="relative overflow-hidden pt-16 pb-12 md:pt-24 md:pb-16 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
                </div>
                <div className="relative max-w-3xl mx-auto px-4 md:px-8 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span>
                        <span className="text-white/80 text-sm font-medium">Real-time tracking enabled</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
                        Track Your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Order</span>
                    </h1>
                    <p className="text-white/60 text-lg mb-10">
                        Enter your order ID to get instant delivery updates.
                    </p>

                    <form onSubmit={(e) => { e.preventDefault(); handleTrack(); }} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="e.g. ORD8D37CF7B"
                                value={orderId}
                                onChange={(e) => setOrderId(e.target.value)}
                                required
                                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-400/60 text-base transition-all"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-8 py-4 rounded-2xl transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-purple-900/30 whitespace-nowrap text-base"
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                                <>Track Now <span className="text-lg">→</span></>
                            )}
                        </button>
                    </form>
                    <p className="text-white/40 text-xs mt-4">Order ID is in your confirmation email or SMS.</p>
                </div>
            </section>

            {/* Result Section */}
            <section className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
                {orderStatus ? (
                    <div className="space-y-6 animate-fade-in">
                        {/* Status Header */}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Order #{orderStatus.order_id}</p>
                                    <h2 className="text-2xl font-bold text-gray-900">{orderStatus.current_status}</h2>
                                </div>
                                <span className={`px-5 py-2 rounded-full text-sm font-semibold border ${statusColor(orderStatus.current_status)}`}>
                                    {orderStatus.current_status}
                                </span>
                            </div>

                            {/* Progress Bar */}
                            {orderStatus.tracking_timeline?.length > 0 && (
                                <div className="mb-4">
                                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                                        <span>Order Placed</span>
                                        <span>{getCompletedCount(orderStatus.tracking_timeline)} / {getTotalSteps(orderStatus.tracking_timeline)} Steps</span>
                                        <span>Delivered</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-700"
                                            style={{ width: `${getProgress(orderStatus.tracking_timeline)}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Meta Info */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                                {orderStatus.last_location && (
                                    <div className="bg-gray-50 rounded-2xl p-4 flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-purple-500 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-xs text-gray-400">Last Location</p>
                                            <p className="font-semibold text-gray-800 text-sm">{orderStatus.last_location}</p>
                                        </div>
                                    </div>
                                )}
                                <div className="bg-gray-50 rounded-2xl p-4 flex items-start gap-3">
                                    <Clock className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400">Last Updated</p>
                                        <p className="font-semibold text-gray-800 text-sm">{formatUpdated(orderStatus.updated_at)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Timeline */}
                        {orderStatus.tracking_timeline?.length > 0 && (
                            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-10">
                                <h3 className="text-lg font-bold text-gray-800 mb-8">Delivery Timeline</h3>
                                <div className="relative">
                                    {/* Vertical line */}
                                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-blue-300 to-gray-200 rounded-full" style={{ zIndex: 0 }} />

                                    <div className="space-y-8">
                                        {orderStatus.tracking_timeline.map((step, idx) => {
                                            const Icon = STEP_ICONS[idx] || Package;
                                            return (
                                                <div key={idx} className="flex gap-5 items-start relative" style={{ zIndex: 1 }}>
                                                    {/* Icon Circle */}
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-all shadow-sm ${step.completed
                                                        ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-blue-200'
                                                        : step.active
                                                            ? 'bg-white border-2 border-purple-500 text-purple-600 scale-110 shadow-purple-100'
                                                            : 'bg-gray-100 text-gray-300 border border-gray-200'
                                                        }`}>
                                                        {step.completed
                                                            ? <CheckCircle className="w-5 h-5" />
                                                            : <Icon className="w-5 h-5" />
                                                        }
                                                    </div>

                                                    {/* Info */}
                                                    <div className="flex-1 pb-2">
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                                            <h4 className={`font-semibold text-base ${step.completed || step.active ? 'text-gray-900' : 'text-gray-400'}`}>
                                                                {step.title}
                                                                {step.active && (
                                                                    <span className="ml-2 inline-block text-[11px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full font-medium align-middle">
                                                                        Active
                                                                    </span>
                                                                )}
                                                            </h4>
                                                            <span className={`text-sm font-mono ${step.completed || step.active ? 'text-gray-500' : 'text-gray-300'}`}>
                                                                {formatTime(step.time) || 'Pending'}
                                                            </span>
                                                        </div>
                                                        {step.description ? (
                                                            <p className={`text-sm mt-1 ${step.active ? 'text-purple-600 font-medium animate-pulse' : 'text-gray-400'}`}>
                                                                {step.description}
                                                            </p>
                                                        ) : !step.completed && !step.active ? (
                                                            <p className="text-sm mt-1 text-gray-300">Awaiting update...</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="text-center">
                            <button
                                onClick={() => { setOrderStatus(null); setOrderId(''); setNotFound(false); }}
                                className="px-8 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 hover:border-purple-400 hover:text-purple-600 font-medium transition-all text-sm"
                            >
                                ← Track Another Order
                            </button>
                        </div>
                    </div>
                ) : notFound ? (
                    <div className="text-center py-20">
                        <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-100">
                            <AlertCircle className="w-12 h-12 text-red-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-3">Order Not Found</h2>
                        <p className="text-gray-500 max-w-sm mx-auto">
                            We couldn't find an order with ID <strong>{orderId}</strong>. Please double-check and try again.
                        </p>
                    </div>
                ) : (
                    <div className="text-center py-20">
                        <div className="relative w-32 h-32 mx-auto mb-8">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full animate-pulse" />
                            <div className="relative w-full h-full flex items-center justify-center">
                                <Truck className="w-16 h-16 text-gray-300" />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-700 mb-3">Ready to Track</h2>
                        <p className="text-gray-400 text-base max-w-sm mx-auto">
                            Enter your Order ID above to see real-time delivery status.
                        </p>
                    </div>
                )}
            </section>
        </div>
    );
}
